import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Textarea } from "design-system/atoms/Textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { cn } from "design-system/utils"
import { previewWebhookBody } from "lopermut"
import { Plus } from "lucide-react"
import { useMemo } from "react"
import { Controller, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { WebhookBodyFormat, WebhookMethod } from "schema"
import { VariableOption } from "../../../hooks/useCapabilityVariables"
import { FieldsForm } from "../FieldsForm"
import {
  addTypeForHeaderFieldsForm,
  getNestedError,
  removeTypeFromHeaderFieldsForm,
  toFieldPath,
  WebhookConfig,
} from "./WebhookRequestUtils"

type Props = {
  form: UseFormReturn<WebhookConfig>
  namePrefix: string
  variables?: VariableOption[]
  disabled?: boolean
}

export function WebhookRequestFields({ form, namePrefix, variables = [], disabled }: Props) {
  const { t } = useTranslation()
  const isChainStep = namePrefix.startsWith("steps.")

  const webhookBody =
    (form.watch(toFieldPath(namePrefix, "webhookBody")) as string | undefined) ?? "{}"
  const webhookBodyFormat =
    (form.watch(toFieldPath(namePrefix, "webhookBodyFormat")) as
      keyof typeof WebhookBodyFormat | undefined) ?? WebhookBodyFormat.json
  const method = form.watch(toFieldPath(namePrefix, "method")) as WebhookConfig["method"]

  const bodyPreview = useMemo(() => {
    return previewWebhookBody(webhookBody, webhookBodyFormat)
  }, [webhookBody, webhookBodyFormat])

  const previewLanguage = webhookBodyFormat === WebhookBodyFormat.json ? "json" : "text"

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-0.5">
        <div className="flex flex-row gap-2">
          <FormField
            control={form.control}
            name={toFieldPath(namePrefix, "method")}
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("capabilities.webhookConfig.method.label")}</FormLabel>
                <FormControl>
                  <Select
                    value={field.value as string}
                    onValueChange={field.onChange}
                    disabled={disabled}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue>{field.value as string}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(WebhookMethod).map((methodOption) => (
                        <SelectItem key={methodOption} value={methodOption}>
                          {methodOption}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={toFieldPath(namePrefix, "url")}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>{t("capabilities.webhookConfig.url.label")}</FormLabel>
                <FormControl>
                  <VaultVariableAutocomplete
                    variables={variables}
                    className="w-full"
                    disabled={disabled || method !== WebhookMethod.GET}
                  >
                    <Input
                      type="url"
                      placeholder={t("capabilities.webhookConfig.url.placeholder")}
                      {...field}
                      value={(field.value as string | undefined) ?? ""}
                      disabled={disabled}
                    />
                  </VaultVariableAutocomplete>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      <Controller
        control={form.control}
        name={toFieldPath(namePrefix, "webhookHeaders")}
        render={({ field }) => (
          <FieldsForm
            label={t("capabilities.webhookConfig.webhookHeaders.label")}
            description={t("capabilities.webhookConfig.webhookHeaders.description")}
            action={
              <Button
                type="button"
                variant="ghost"
                disabled={disabled}
                onClick={() =>
                  field.onChange([
                    ...((field.value as Array<{ name: string; value?: string }>) || []),
                    {
                      name: "",
                      value: "",
                    },
                  ])
                }
                className="w-fit"
              >
                <Plus className="size-4" />
                {t("capabilities.webhookConfig.fieldsForm.addField")}
              </Button>
            }
            value={addTypeForHeaderFieldsForm(
              field.value as Array<{ name: string; value?: string }>,
            )}
            onChange={(fields) => field.onChange(removeTypeFromHeaderFieldsForm(fields))}
            required={false}
            minFields={0}
            error={getNestedError(form.formState.errors, namePrefix, "webhookHeaders")}
            mode="keyValue"
            variables={variables}
          />
        )}
      />
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-1 flex-col gap-1.5">
          <div className="flex flex-row items-center justify-between gap-2">
            <Label>{t("capabilities.webhookConfig.webhookBody.label")}</Label>
            <FormField
              control={form.control}
              name={toFieldPath(namePrefix, "webhookBodyFormat")}
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormControl>
                    <Select
                      value={(field.value as string | undefined) ?? WebhookBodyFormat.json}
                      onValueChange={field.onChange}
                      disabled={disabled || method === WebhookMethod.GET}
                    >
                      <SelectTrigger className="w-52">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(WebhookBodyFormat).map((format) => (
                          <SelectItem key={format} value={format}>
                            {t(`capabilities.webhookConfig.webhookBody.format.${format}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {t(
              isChainStep
                ? `capabilities.webhookConfig.webhookBody.descriptionChain.${webhookBodyFormat}`
                : `capabilities.webhookConfig.webhookBody.description.${webhookBodyFormat}`,
            )}
          </p>
        </div>
        <div className="flex md:flex-row flex-col gap-4">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <Controller
              control={form.control}
              name={toFieldPath(namePrefix, "webhookBody")}
              render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <Label>{t("capabilities.webhookConfig.webhookBody.templateInput")}</Label>
                  <VaultVariableAutocomplete variables={variables} className="w-full">
                    <Textarea
                      value={(field.value as string | undefined) ?? "{}"}
                      placeholder={t(
                        isChainStep
                          ? `capabilities.webhookConfig.webhookBody.freeJsonPlaceholderChain.${webhookBodyFormat}`
                          : `capabilities.webhookConfig.webhookBody.freeJsonPlaceholder.${webhookBodyFormat}`,
                      )}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation()
                        }
                      }}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="font-mono resize-none min-h-[180px]"
                      style={{ tabSize: 2 }}
                      disabled={disabled || method === WebhookMethod.GET}
                    />
                  </VaultVariableAutocomplete>
                </div>
              )}
            />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <Label>{t("capabilities.webhookConfig.webhookBody.templatePreview")}</Label>
            <MarkdownRenderer
              className={cn(
                "min-w-0 max-w-full overflow-auto rounded-md border",
                method === WebhookMethod.GET && "opacity-50",
              )}
              cardClassName="border-none shadow-none min-w-0 max-w-full"
              codeClassName="border-none whitespace-pre-wrap break-all"
              shortenTextAfterLength={0}
            >
              {`\`\`\`${previewLanguage}\n${bodyPreview ?? t("capabilities.webhookConfig.webhookBody.invalidTemplate")}\n\`\`\``}
            </MarkdownRenderer>
          </div>
        </div>
      </div>
    </div>
  )
}
